import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import productRepository from '../../services/repositories/product.repository';
import { buildImageUrl } from '../../utils/imageUrl';
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap';
import '../../styles/products.scss';

const ProductIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewProductLoading, setViewProductLoading] = useState(false);
  const [viewProductError, setViewProductError] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [viewSaveLoading, setViewSaveLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productRepository.getAll();
      
      // Transform API data to match DataTable format
      const transformedProducts = Array.isArray(response) ? response : (response.data || []);
      
      const formattedProducts = transformedProducts.map(product => {
        // Get main image URL
        let mainImageUrl = null;
        if (product.main_image && product.main_image.image_path) {
          mainImageUrl = buildImageUrl(product.main_image.image_path);
        } else if (product.images && product.images.length > 0) {
          const mainImg = product.images.find(img => img.is_main) || product.images[0];
          if (mainImg && mainImg.image_path) {
            mainImageUrl = buildImageUrl(mainImg.image_path);
          }
        }
        
        // Get gallery images URLs
        const galleryImages = [];
        if (product.gallery_images && product.gallery_images.length > 0) {
          product.gallery_images.forEach(img => {
            if (img.image_path) {
              galleryImages.push(buildImageUrl(img.image_path));
            }
          });
        }
        
        return {
          id: product.id,
          name: product.name,
          price: parseFloat(product.price) || 0,
          discount: parseFloat(product.discount) || 0,
          sku: product.sku || '-',
          status: product.status || 'active',
          barcode: product.barcode || '-',
          images: mainImageUrl ? [mainImageUrl] : [],
          detail_images: galleryImages,
          qte: product.stock || 0, // Map stock to qte for DataTable
          weight: product.weight || '-',
          stock_alert: product.stock_alert || 0,
          category: product.category || product.category_id || null,
          warehouse: product.warehouse || product.warehouse_id || null,
        };
      });
      
      setProducts(formattedProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Error loading products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Define columns for DataTable - useMemo to prevent re-creation
  const columns = useMemo(() => [
    {
      header: 'Image',
      accessor: 'images',
      type: 'image',
      sortable: false,
    },
    {
      header: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      header: 'SKU',
      accessor: 'sku',
      sortable: true,
    },
    {
      header: 'Price',
      accessor: 'price',
      sortable: true,
      render: (value) => `${value} DH`,
    },
    {
      header: 'Discount',
      accessor: 'discount',
      sortable: true,
      render: (value) => value > 0 ? `${value}%` : '-',
    },
    {
      header: 'Quantity',
      accessor: 'qte',
      sortable: true,
    },
    {
      header: 'Category',
      accessor: 'category',
      sortable: true,
    },
    {
      header: 'Warehouse',
      accessor: 'warehouse',
      sortable: true,
    },
    {
      header: 'Status',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
  ], []);

  // Define relations for DataTable - useMemo to prevent re-creation
  const relations = useMemo(() => ({
    category: {
      table: 'category',
      accessor: 'category',
      displayField: 'name',
      valueField: 'id',
    },
    warehouse: {
      table: 'warehouse',
      accessor: 'warehouse',
      displayField: 'name',
      valueField: 'id',
    },
  }), []);

  const handleAdd = useCallback(() => {
    navigate('/admin/products/product-add');
  }, [navigate]);

  const handleEdit = useCallback((product) => {
    navigate(`/admin/products/product-edit/${product.id}`);
  }, [navigate]);

  const handleView = useCallback((product) => {
    const open = async () => {
      try {
        setViewModalOpen(true);
        setViewProductError(null);
        setViewProductLoading(true);
        setViewProduct(null);

        const response = await productRepository.getById(product.id);
        const p = response.data || response;
        setViewProduct(p);
      } catch (err) {
        console.error('Error fetching product details:', err);
        setViewProductError(err.message || 'Error loading product details');
      } finally {
        setViewProductLoading(false);
      }
    };
    open();
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewProduct(null);
    setViewProductError(null);
    setViewProductLoading(false);
    setViewSaveLoading(false);
  }, []);

  const handleToggleRegionAvailability = (key) => {
    setViewProduct((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: !Boolean(prev[key]) };
    });
  };

  const handleSaveRegionAvailability = async () => {
    if (!viewProduct?.id) return;
    try {
      setViewSaveLoading(true);
      setViewProductError(null);
      await productRepository.update(viewProduct.id, {
        disabled_fes: viewProduct.disabled_fes ? 1 : 0,
        disabled_casa: viewProduct.disabled_casa ? 1 : 0,
      });
      await fetchProducts();
    } catch (err) {
      console.error('Error saving region availability:', err);
      setViewProductError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setViewSaveLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    const config = {
      active: { variant: 'success', label: 'Active' },
      inactive: { variant: 'danger', label: 'Inactive' },
      out_of_stock: { variant: 'warning', label: 'Out of stock' },
    }[s] || { variant: 'secondary', label: status || '-' };
    return (
      <Badge bg="transparent" className={`bg-soft-${config.variant} text-${config.variant}`}>
        {config.label}
      </Badge>
    );
  };

  const getMainImageUrl = (p) => {
    if (!p) return null;
    if (p.main_image?.image_path) return buildImageUrl(p.main_image.image_path);
    if (Array.isArray(p.images) && p.images.length > 0) {
      const main = p.images.find((img) => img?.is_main) || p.images[0];
      if (main?.image_path) return buildImageUrl(main.image_path);
    }
    return null;
  };

  const getGalleryUrls = (p) => {
    if (!p) return [];
    const imgs = Array.isArray(p.gallery_images) ? p.gallery_images : [];
    return imgs
      .map((img) => (img?.image_path ? buildImageUrl(img.image_path) : null))
      .filter(Boolean);
  };

  const handleDelete = useCallback(async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      try {
        setLoading(true);
        await productRepository.delete(product.id);
        alert('Product deleted successfully');
        // Refetch data from API after deletion
        await fetchProducts();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Error deleting product');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchProducts]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} products?`)) {
      try {
        setLoading(true);
        await productRepository.bulkDelete(selectedIds);
        alert(`${selectedIds.length} products deleted successfully`);
        // Refetch data from API after bulk deletion
        await fetchProducts();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Error deleting products');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchProducts]);

  useEffect(() => {
    setTitle('Products');
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="products-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={products}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        relations={relations}
        title="Products List"
        addButtonText="Add New Product"
        searchPlaceholder="Search products..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />

      <Modal
        show={viewModalOpen}
        onHide={handleCloseViewModal}
        centered
        size="lg"
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header closeButton className="px-24 py-20">
          <Modal.Title style={{ fontSize: '16px', fontWeight: 600 }}>
            Détails du Produit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-24 py-20">
          {viewProductLoading ? (
            <div className="text-center py-40">
              <Spinner animation="border" role="status" />
              <div className="mt-12 text-muted">Chargement...</div>
            </div>
          ) : viewProductError ? (
            <div className="alert alert-danger" role="alert">
              {viewProductError}
            </div>
          ) : viewProduct ? (
            <>
              <Row className="g-4">
                <Col lg={4}>
                  <div className="border border-gray-100 rounded-12 p-16 bg-light-subtle">
                    {getMainImageUrl(viewProduct) ? (
                      <img
                        src={getMainImageUrl(viewProduct)}
                        alt={viewProduct.name || 'Produit'}
                        className="img-fluid rounded"
                        style={{ width: '100%', height: '220px', objectFit: 'contain', background: '#fff' }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center rounded"
                        style={{ height: '220px', background: '#fff' }}
                      >
                        <span className="text-muted">Aucune image</span>
                      </div>
                    )}

                    {getGalleryUrls(viewProduct).length > 0 && (
                      <div className="d-flex gap-8 mt-12 flex-wrap">
                        {getGalleryUrls(viewProduct).slice(0, 6).map((url) => (
                          <img
                            key={url}
                            src={url}
                            alt="Galerie"
                            className="rounded border"
                            style={{ width: '46px', height: '46px', objectFit: 'cover', background: '#fff' }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </Col>

                <Col lg={8}>
                  <div className="border border-gray-100 rounded-12 p-16">
                    <div className="d-flex align-items-start justify-content-between gap-12">
                      <div>
                        <div style={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2 }}>
                          {viewProduct.name || '-'}
                        </div>
                        <div className="text-muted mt-4" style={{ fontSize: '13px' }}>
                          SKU: {viewProduct.sku || '-'} {viewProduct.barcode ? `• Barcode: ${viewProduct.barcode}` : ''}
                        </div>
                      </div>
                      <div>{getStatusBadge(viewProduct.status)}</div>
                    </div>

                    <Row className="g-3 mt-12">
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Prix (Fès)</div>
                        <div style={{ fontWeight: 700 }}>{Number(viewProduct.price || 0).toFixed(2)} DH</div>
                      </Col>
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Prix (Casa)</div>
                        <div style={{ fontWeight: 700 }}>{Number(viewProduct.price_casa || 0).toFixed(2)} DH</div>
                      </Col>
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Stock</div>
                        <div style={{ fontWeight: 700 }}>{viewProduct.stock ?? '-'}</div>
                      </Col>
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Remise</div>
                        <div style={{ fontWeight: 700 }}>{viewProduct.discount ? `${viewProduct.discount}%` : '-'}</div>
                      </Col>
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Catégorie</div>
                        <div style={{ fontWeight: 600 }}>
                          {viewProduct.category?.name || viewProduct.category || viewProduct.category_id || '-'}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Entrepôt</div>
                        <div style={{ fontWeight: 600 }}>
                          {viewProduct.warehouse?.name || viewProduct.warehouse || viewProduct.warehouse_id || '-'}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Poids / Volume</div>
                        <div style={{ fontWeight: 600 }}>{viewProduct.weight || '-'}</div>
                      </Col>
                    </Row>
                  </div>

                  <div className="border border-gray-100 rounded-12 p-16 mt-16">
                    <div className="mb-12" style={{ fontWeight: 700 }}>
                      Disponibilité par région
                    </div>
                    <Row className="g-3">
                      <Col sm={6}>
                        <button
                          type="button"
                          onClick={() => handleToggleRegionAvailability('disabled_fes')}
                          className={`w-100 border rounded p-12 d-flex align-items-start gap-10 bg-light-subtle text-start ${
                            viewProduct.disabled_fes ? 'border-primary' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={`border rounded d-flex align-items-center justify-content-center flex-shrink-0 ${
                            viewProduct.disabled_fes ? 'border-primary text-primary' : 'text-muted'
                          }`} style={{ width: 18, height: 18, background: '#fff', marginTop: 2 }}>
                            {viewProduct.disabled_fes ? '✓' : ''}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700 }}>Fès</div>
                            <div className="text-muted" style={{ fontSize: '13px' }}>
                              {viewProduct.disabled_fes ? 'Désactivé' : 'Actif'}
                            </div>
                          </div>
                        </button>
                      </Col>
                      <Col sm={6}>
                        <button
                          type="button"
                          onClick={() => handleToggleRegionAvailability('disabled_casa')}
                          className={`w-100 border rounded p-12 d-flex align-items-start gap-10 bg-light-subtle text-start ${
                            viewProduct.disabled_casa ? 'border-primary' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={`border rounded d-flex align-items-center justify-content-center flex-shrink-0 ${
                            viewProduct.disabled_casa ? 'border-primary text-primary' : 'text-muted'
                          }`} style={{ width: 18, height: 18, background: '#fff', marginTop: 2 }}>
                            {viewProduct.disabled_casa ? '✓' : ''}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700 }}>Casablanca</div>
                            <div className="text-muted" style={{ fontSize: '13px' }}>
                              {viewProduct.disabled_casa ? 'Désactivé' : 'Actif'}
                            </div>
                          </div>
                        </button>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-12">
                      <Button
                        variant="primary"
                        onClick={handleSaveRegionAvailability}
                        disabled={viewSaveLoading}
                        style={{ backgroundColor: '#b8934a', borderColor: '#b8934a' }}
                      >
                        {viewSaveLoading ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  </div>

                  {viewProduct.description && (
                    <div className="border border-gray-100 rounded-12 p-16 mt-16">
                      <div className="mb-10" style={{ fontWeight: 700 }}>Description</div>
                      <div
                        className="text-gray-700"
                        style={{ fontSize: '14px', lineHeight: 1.55 }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewProduct.description) }}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="px-24 py-16">
          <Button
            variant="outline-secondary"
            onClick={handleCloseViewModal}
            className="modal-fermer-btn"
            style={{
              backgroundColor: '#f3f4f6',
              borderColor: '#d1d5db',
              color: '#000',
              '--bs-btn-color': '#000',
              '--bs-btn-hover-color': '#000',
              '--bs-btn-active-color': '#000',
            }}
          >
            Fermer
          </Button>
          {viewProduct?.id && (
            <Button
              variant="primary"
              onClick={() => {
                handleCloseViewModal();
                navigate(`/admin/products/product-edit/${viewProduct.id}`);
              }}
              style={{ backgroundColor: '#b8934a', borderColor: '#b8934a' }}
            >
              Modifier
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductIndex;
